import { useList, useDelete, useNavigation, useOne, useCreate, useUpdate } from '@refinedev/core';
import { useParams, useNavigate } from 'react-router';
import { useState, useEffect } from 'react';
import { FileText, Plus, Edit, Trash2, Loader2, Save, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { ImageManager, type ManagedImage } from '../../components/ImageManager';
import { BilingualField } from '../../components/BilingualField';

// Helper function to generate slug from title
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

interface Page {
  id: string;
  slug: string;
  titleEn?: string;
  titleId?: string;
  title_en?: string;
  title_id?: string;
  descriptionEn?: string;
  descriptionId?: string;
  description_en?: string;
  description_id?: string;
  contentEn?: string;
  contentId?: string;
  content_en?: string;
  content_id?: string;
  metaTitleEn?: string;
  metaTitleId?: string;
  meta_title_en?: string;
  meta_title_id?: string;
  metaDescriptionEn?: string;
  metaDescriptionId?: string;
  meta_description_en?: string;
  meta_description_id?: string;
  heroImage?: string;
  hero_image?: string;
  sortOrder?: number;
  sort_order?: number;
  status?: string;
}

export function PagesList() {
  const { data, isLoading, error } = useList<Page>({ resource: 'pages' });
  const { mutate: deleteOne } = useDelete();
  const { edit, create } = useNavigation();

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this page?')) {
      deleteOne({ resource: 'pages', id });
    }
  };

  if (isLoading) return <div className="p-4">Loading...</div>;
  if (error) return (
    <div className="p-4"><div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">Error loading pages: {error.message}</p></div></div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="w-7 h-7 text-gold-600" />
          <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
        </div>
        <button onClick={() => create('pages')} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors">
          <Plus className="w-5 h-5" /> Add Page
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Slug</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {data?.data.map((page) => {
              const title = page.titleId || page.title_id || page.titleEn || page.title_en || 'Untitled';
              const status = page.status || 'active';
              return (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{title}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{page.slug}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {status === 'active' ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => edit('pages', page.id)} className="p-2 text-gray-400 hover:text-gold-600 transition-colors" title="Edit"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(page.id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data?.data.length === 0 && <div className="p-8 text-center text-gray-500">No pages found. Click "Add Page" to create one.</div>}
      </div>
    </div>
  );
}

// Create Page
export function PagesCreate() {
  const navigate = useNavigate();
  const { mutate: createPage, isPending } = useCreate();
  const [form, setForm] = useState({
    slug: '',
    titleEn: '',
    titleId: '',
    descriptionEn: '',
    descriptionId: '',
    contentEn: '',
    contentId: '',
    metaTitleEn: '',
    metaTitleId: '',
    metaDescriptionEn: '',
    metaDescriptionId: '',
    sortOrder: 0,
    status: 'active',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.titleId) { alert('Please fill in the title'); return; }
    const slug = form.slug || generateSlug(form.titleId);
    createPage(
      { resource: 'pages', values: { ...form, slug } },
      { onSuccess: (data) => { const newId = data?.data?.id; navigate(newId ? `/pages/edit/${newId}` : '/pages'); } }
    );
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/pages')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Create Page</h1>
          <p className="text-sm text-gray-500 mt-1">Add a new dynamic page</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
          <BilingualField label="Title" valueEn={form.titleEn} valueId={form.titleId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'titleEn' : 'titleId']: value })} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
            <input type="text" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder={form.titleId ? generateSlug(form.titleId) : 'auto-generated'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
          </div>
          <BilingualField label="Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={3} />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Content</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content (EN)</label>
            <textarea value={form.contentEn} onChange={(e) => setForm({ ...form, contentEn: e.target.value })} rows={10} placeholder="Page content in English (HTML supported)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent font-mono text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content (ID)</label>
            <textarea value={form.contentId} onChange={(e) => setForm({ ...form, contentId: e.target.value })} rows={10} placeholder="Page content in Indonesian (HTML supported)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent font-mono text-sm" />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">SEO</h2>
          <BilingualField label="Meta Title" valueEn={form.metaTitleEn} valueId={form.metaTitleId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'metaTitleEn' : 'metaTitleId']: value })} />
          <BilingualField label="Meta Description" valueEn={form.metaDescriptionEn} valueId={form.metaDescriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'metaDescriptionEn' : 'metaDescriptionId']: value })} multiline rows={2} />
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800"><strong>Note:</strong> You can add a hero image after creating the page.</p>
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/pages')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
            {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Create & Continue
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Page
export function PagesEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading } = useOne<Page>({ resource: 'pages', id: id! });
  const { mutate: updatePage, isPending } = useUpdate();

  const [form, setForm] = useState({
    slug: '',
    titleEn: '',
    titleId: '',
    descriptionEn: '',
    descriptionId: '',
    contentEn: '',
    contentId: '',
    metaTitleEn: '',
    metaTitleId: '',
    metaDescriptionEn: '',
    metaDescriptionId: '',
    sortOrder: 0,
    status: 'active',
  });
  const [images, setImages] = useState<ManagedImage[]>([]);

  useEffect(() => {
    if (data?.data) {
      const page = data.data;
      setForm({
        slug: page.slug || '',
        titleEn: page.titleEn || page.title_en || '',
        titleId: page.titleId || page.title_id || '',
        descriptionEn: page.descriptionEn || page.description_en || '',
        descriptionId: page.descriptionId || page.description_id || '',
        contentEn: page.contentEn || page.content_en || '',
        contentId: page.contentId || page.content_id || '',
        metaTitleEn: page.metaTitleEn || page.meta_title_en || '',
        metaTitleId: page.metaTitleId || page.meta_title_id || '',
        metaDescriptionEn: page.metaDescriptionEn || page.meta_description_en || '',
        metaDescriptionId: page.metaDescriptionId || page.meta_description_id || '',
        sortOrder: page.sortOrder || page.sort_order || 0,
        status: page.status || 'active',
      });
      const heroImage = page.heroImage || page.hero_image;
      if (heroImage) {
        setImages([{ url: heroImage, imageType: 'main' as const, sortOrder: 0 }]);
      }
    }
  }, [data]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const heroImage = images.length > 0 ? images[0].url : '';
    updatePage(
      { resource: 'pages', id: id!, values: { ...form, heroImage } },
      { onSuccess: () => navigate('/pages') }
    );
  };

  if (isLoading) return <div className="p-4">Loading...</div>;

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/pages')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Edit Page</h1>
          <p className="text-sm text-gray-500 mt-1">{form.titleId || form.titleEn || 'Page details'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={handleSubmit} className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
            <BilingualField label="Title" valueEn={form.titleEn} valueId={form.titleId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'titleEn' : 'titleId']: value })} required />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input type="text" value={form.slug} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent bg-gray-50" readOnly />
            </div>
            <BilingualField label="Description" valueEn={form.descriptionEn} valueId={form.descriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'descriptionEn' : 'descriptionId']: value })} multiline rows={3} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Content</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (EN)</label>
              <textarea value={form.contentEn} onChange={(e) => setForm({ ...form, contentEn: e.target.value })} rows={10} placeholder="Page content in English (HTML supported)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent font-mono text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content (ID)</label>
              <textarea value={form.contentId} onChange={(e) => setForm({ ...form, contentId: e.target.value })} rows={10} placeholder="Page content in Indonesian (HTML supported)" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent font-mono text-sm" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">SEO</h2>
            <BilingualField label="Meta Title" valueEn={form.metaTitleEn} valueId={form.metaTitleId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'metaTitleEn' : 'metaTitleId']: value })} />
            <BilingualField label="Meta Description" valueEn={form.metaDescriptionEn} valueId={form.metaDescriptionId} onChange={(lang, value) => setForm({ ...form, [lang === 'en' ? 'metaDescriptionEn' : 'metaDescriptionId']: value })} multiline rows={2} />
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" onClick={() => navigate('/pages')} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
            <button type="submit" disabled={isPending} className="flex items-center gap-2 px-4 py-2 bg-gold-600 text-white rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50">
              {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save Changes
            </button>
          </div>
        </form>

        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Hero Image</h2>
            <ImageManager entityType="pages" entityId={id!} images={images} imageType="main" multiple={false} maxImages={1} onChange={setImages} />
          </div>
        </div>
      </div>
    </div>
  );
}
